import { useCallback, useState } from "react";

// Per-user, per-dashboard widget layout (order + visibility) persisted in localStorage.
// `defaults` is an array of { id, label }. New defaults are appended, removed ones dropped.
export function useDashboardLayout(key, defaults) {
  const storageKey = `pq_dash_${key}`;

  const read = useCallback(() => {
    const base = defaults.map((d) => ({ ...d, visible: true }));
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey));
      if (!Array.isArray(saved)) return base;
      const byId = Object.fromEntries(defaults.map((d) => [d.id, d]));
      const merged = saved.filter((s) => byId[s.id]).map((s) => ({ ...byId[s.id], visible: s.visible !== false }));
      const seen = new Set(merged.map((m) => m.id));
      defaults.forEach((d) => { if (!seen.has(d.id)) merged.push({ ...d, visible: true }); });
      return merged;
    } catch {
      return base;
    }
  }, [storageKey, defaults]);

  const [items, setItems] = useState(read);

  const persist = useCallback((next) => {
    setItems(next);
    try { localStorage.setItem(storageKey, JSON.stringify(next.map((i) => ({ id: i.id, visible: i.visible })))); } catch { /* ignore */ }
  }, [storageKey]);

  const move = useCallback((id, dir) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.id === id);
      const to = idx + dir;
      if (idx < 0 || to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[to]] = [next[to], next[idx]];
      try { localStorage.setItem(storageKey, JSON.stringify(next.map((i) => ({ id: i.id, visible: i.visible })))); } catch { /* ignore */ }
      return next;
    });
  }, [storageKey]);

  const toggle = useCallback((id) => {
    setItems((prev) => {
      const next = prev.map((i) => (i.id === id ? { ...i, visible: !i.visible } : i));
      try { localStorage.setItem(storageKey, JSON.stringify(next.map((i) => ({ id: i.id, visible: i.visible })))); } catch { /* ignore */ }
      return next;
    });
  }, [storageKey]);

  const reset = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
    setItems(defaults.map((d) => ({ ...d, visible: true })));
  }, [storageKey, defaults]);

  return { items, move, toggle, reset };
}
